import React, { useCallback, useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Chip,
	CircularProgress,
	Collapse,
	Grid,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EventIcon from '@mui/icons-material/Event';
import { useHistory } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import {
	IProviderPayableLineListFilters,
	IProviderPayableLineScheduleFilters,
	IProviderPayableLineScheduleGroup,
	ProviderPayableLinePaymentStatusEnum,
} from 'features/salesOrder/types';
import { getProviderPayableLineSchedule } from 'features/salesOrder/services';
import { ProviderPayablesFilters } from '../ProviderPayablesList/ProviderPayablesFilters';
import { ScheduleQuickDateBar } from './ScheduleQuickDateBar';
import routes from 'constants/routes';
import { openPaymentsUrl } from 'pages/SalesOrder/SalesOrderScreen/helpers';
import { extractErrorMessage } from '../../../quotation/helpers';
import {
	PROVIDER_PAYABLE_LINE_STATUS_COLOR,
	PROVIDER_PAYABLE_LINE_STATUS_LABEL,
	PROVIDER_PAYABLE_LINE_TYPE_LABEL,
} from '../../constants';
import { CheckInDatePreset, formatDateYmd, getDateRangeFromPreset } from 'features/common/helpers/datePresets';

const CRM_DEAL_URL = 'https://crm.mevuelo.com/dashboard/cases/';

const CopyButton = ({ value }: { value: string }) => {
	const [copied, setCopied] = useState(false);
	const handleCopy = (e: React.MouseEvent) => {
		e.stopPropagation();
		navigator.clipboard.writeText(value).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		});
	};
	return (
		<Tooltip title={copied ? '¡Copiado!' : 'Copiar'}>
			<IconButton size="small" onClick={handleCopy} sx={{ ml: 0.5, p: 0.25 }}>
				<ContentCopyIcon sx={{ fontSize: 14, color: copied ? 'success.main' : 'action.active' }} />
			</IconButton>
		</Tooltip>
	);
};

const formatScheduleDate = (dateStr: string): string => {
	try {
		// date-fns requiere comillas simples dentro del formato para texto literal — se mantiene el string con comillas dobles
		// eslint-disable-next-line quotes
		return format(parseISO(dateStr), "EEEE d 'de' MMMM yyyy", { locale: es });
	} catch {
		return dateStr;
	}
};

const isPast = (dateStr: string): boolean => {
	try {
		return parseISO(dateStr) < new Date();
	} catch {
		return false;
	}
};

interface ScheduleGroupCardProps {
	group: IProviderPayableLineScheduleGroup;
	defaultExpanded?: boolean;
}

const ScheduleGroupCard = ({ group, defaultExpanded = true }: ScheduleGroupCardProps) => {
	const history = useHistory();
	const [expanded, setExpanded] = useState(defaultExpanded);
	const past = isPast(group.schedulePaymentDate);

	const totalByStatus = group.lines.reduce(
		(acc, line) => {
			acc[line.paymentStatus] = (acc[line.paymentStatus] ?? 0) + line.scheduleAmount;
			return acc;
		},
		{} as Record<string, number>,
	);

	return (
		<Paper
			variant="outlined"
			sx={{
				mb: 2,
				borderRadius: 2,
				borderColor: past ? 'warning.light' : 'primary.light',
				overflow: 'hidden',
			}}
		>
			{/* Header del grupo */}
			<Box
				display="flex"
				alignItems="center"
				justifyContent="space-between"
				px={2}
				py={1.25}
				sx={{
					bgcolor: past ? 'warning.50' : 'primary.50',
					cursor: 'pointer',
					userSelect: 'none',
					'&:hover': { bgcolor: past ? 'warning.100' : 'primary.100' },
				}}
				onClick={() => setExpanded((v) => !v)}
			>
				<Box display="flex" alignItems="center" gap={1.5}>
					<EventIcon sx={{ color: past ? 'warning.main' : 'primary.main', fontSize: 20 }} />
					<Box>
						<Typography variant="subtitle2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
							{formatScheduleDate(group.schedulePaymentDate)}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{group.lines.length} línea{group.lines.length !== 1 ? 's' : ''}
						</Typography>
					</Box>
					{past && <Chip label="Vencido" color="warning" size="small" variant="outlined" sx={{ fontWeight: 600 }} />}
				</Box>

				<Box display="flex" alignItems="center" gap={2}>
					{/* Breakdown por estado */}
					<Box display="flex" gap={1} flexWrap="wrap">
						{Object.entries(totalByStatus).map(([status, amount]) => (
							<Chip
								key={status}
								label={`${
									PROVIDER_PAYABLE_LINE_STATUS_LABEL[status as ProviderPayableLinePaymentStatusEnum]
								}: USD ${amount.toLocaleString('es-UY', { minimumFractionDigits: 2 })}`}
								color={PROVIDER_PAYABLE_LINE_STATUS_COLOR[status as ProviderPayableLinePaymentStatusEnum]}
								size="small"
								variant="outlined"
							/>
						))}
					</Box>
					<Box textAlign="right">
						<Typography variant="body2" fontWeight={700} color={past ? 'warning.dark' : 'primary.dark'}>
							USD {group.totalAmount.toLocaleString('es-UY', { minimumFractionDigits: 2 })}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							total
						</Typography>
					</Box>
					{expanded ? (
						<ExpandLessIcon sx={{ color: 'text.secondary' }} />
					) : (
						<ExpandMoreIcon sx={{ color: 'text.secondary' }} />
					)}
				</Box>
			</Box>

			{/* Tabla de líneas */}
			<Collapse in={expanded}>
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow sx={{ bgcolor: 'grey.50' }}>
								<TableCell>Proveedor</TableCell>
								<TableCell>Tipo</TableCell>
								<TableCell>Ticket</TableCell>
								<TableCell>Pasajero</TableCell>
								<TableCell>Orden / Deal</TableCell>
								<TableCell align="right">Monto schedule</TableCell>
								<TableCell align="right">Pendiente total</TableCell>
								<TableCell align="center">Estado</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{group.lines.map((line) => (
								<TableRow
									key={`${line.id}-${line.paymentLineIndex}`}
									hover
									onClick={(event) => openPaymentsUrl(history, line.saleOrderId, line.id, event)}
									sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'primary.50' } }}
								>
									<TableCell>
										<Typography variant="body2">{line.providerName ?? line.providerId}</Typography>
									</TableCell>

									<TableCell>
										<Typography variant="caption" color="text.secondary">
											{PROVIDER_PAYABLE_LINE_TYPE_LABEL[line.payableType]}
										</Typography>
									</TableCell>

									<TableCell>
										{line.ticket ? (
											<Typography variant="body2" fontFamily="monospace">
												{line.ticket}
											</Typography>
										) : (
											<Typography variant="body2" color="text.secondary">
												—
											</Typography>
										)}
									</TableCell>

									<TableCell>
										<Typography variant="body2">{line.passengerName ?? '—'}</Typography>
									</TableCell>

									<TableCell>
										<Box display="flex" alignItems="center" gap={0.5}>
											<Tooltip title="Ir a la orden de venta">
												<Typography
													variant="body2"
													color="primary"
													sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
													onClick={(e) => {
														e.stopPropagation();
														history.push(routes.salesOrder.edit.replace(':id', line.saleOrderId));
													}}
												>
													{line.saleOrderId.substring(0, 8)}…
												</Typography>
											</Tooltip>
											<Tooltip title="Abrir en nueva pestaña">
												<IconButton
													size="small"
													sx={{ p: 0.25 }}
													onClick={(e) => {
														e.stopPropagation();
														window.open(
															`${window.location.origin}${routes.salesOrder.edit.replace(':id', line.saleOrderId)}`,
															'_blank',
														);
													}}
												>
													<OpenInNewIcon sx={{ fontSize: 14 }} />
												</IconButton>
											</Tooltip>
											<CopyButton value={line.saleOrderId} />
											{line.dealId && !line.isBitrixDeal && (
												<Tooltip title="Abrir en CRM">
													<Typography
														variant="caption"
														color="text.secondary"
														sx={{ cursor: 'pointer' }}
														onClick={(e) => {
															e.stopPropagation();
															window.open(`${CRM_DEAL_URL}${line.dealId}`, '_blank', 'noopener,noreferrer');
														}}
													>
														#{line.dealId}
													</Typography>
												</Tooltip>
											)}
										</Box>
									</TableCell>

									<TableCell align="right">
										<Typography variant="body2" fontWeight={600} color="primary.main">
											{line.currency} {line.scheduleAmount.toLocaleString('es-UY', { minimumFractionDigits: 2 })}
											{line.paymentLineIndex > 0 && (
												<Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
													(cuota {line.paymentLineIndex + 1})
												</Typography>
											)}
										</Typography>
									</TableCell>

									<TableCell align="right">
										<Typography
											variant="body2"
											color={line.pending > 0 ? 'warning.main' : line.pending < 0 ? 'error.main' : 'success.main'}
											fontWeight="bold"
										>
											{line.currency} {line.pending.toLocaleString('es-UY', { minimumFractionDigits: 2 })}
										</Typography>
									</TableCell>

									<TableCell align="center">
										<Chip
											label={PROVIDER_PAYABLE_LINE_STATUS_LABEL[line.paymentStatus]}
											color={PROVIDER_PAYABLE_LINE_STATUS_COLOR[line.paymentStatus]}
											size="small"
										/>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</Collapse>
		</Paper>
	);
};

interface ProviderPayablesScheduleViewProps {
	fixedProviderId?: string;
}

const buildScheduleFilters = (
	listFilters: IProviderPayableLineListFilters,
	scheduleDateFrom: string,
	scheduleDateTo: string,
): IProviderPayableLineScheduleFilters => ({
	providerIds: listFilters.providerIds,
	ticket: listFilters.ticket,
	saleOrderId: listFilters.saleOrderId,
	sellerMarket: listFilters.sellerMarket,
	payableTypes: listFilters.payableTypes,
	paymentTypes: listFilters.paymentTypes,
	paymentStatuses: listFilters.paymentStatuses,
	dateFrom: listFilters.dateFrom,
	dateTo: listFilters.dateTo,
	scheduleDateFrom,
	scheduleDateTo,
});

export const ProviderPayablesScheduleView = ({ fixedProviderId }: ProviderPayablesScheduleViewProps): JSX.Element => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [groups, setGroups] = useState<IProviderPayableLineScheduleGroup[]>([]);
	const [activePreset, setActivePreset] = useState<CheckInDatePreset | ''>('thisWeek');
	const [listFilters, setListFilters] = useState<IProviderPayableLineListFilters>(() =>
		fixedProviderId ? { providerIds: [fixedProviderId] } : {},
	);

	// Fechas de schedule derivadas del preset activo o de los filtros manuales del listado
	const [scheduleDateFrom, setScheduleDateFrom] = useState<string>(() => {
		const range = getDateRangeFromPreset('thisWeek');
		return formatDateYmd(range.from);
	});
	const [scheduleDateTo, setScheduleDateTo] = useState<string>(() => {
		const range = getDateRangeFromPreset('thisWeek');
		return formatDateYmd(range.to);
	});

	const loadData = useCallback(async () => {
		if (!scheduleDateFrom || !scheduleDateTo) return;

		try {
			setLoading(true);
			setError(null);
			const filters = buildScheduleFilters(listFilters, scheduleDateFrom, scheduleDateTo);
			const res = await getProviderPayableLineSchedule(filters);
			setGroups(res.data.paymentSchedule ?? []);
		} catch (err) {
			setError(extractErrorMessage(err, 'No se pudo cargar la agenda de pagos. Intentá de nuevo.'));
		} finally {
			setLoading(false);
		}
	}, [listFilters, scheduleDateFrom, scheduleDateTo]);

	useEffect(() => {
		loadData();
	}, [loadData]);

	const handlePresetToggle = (preset: CheckInDatePreset) => {
		if (activePreset === preset) {
			setActivePreset('');
			return;
		}
		const range = getDateRangeFromPreset(preset);
		setScheduleDateFrom(formatDateYmd(range.from));
		setScheduleDateTo(formatDateYmd(range.to));
		setActivePreset(preset);
	};

	const handleFiltersChange = useCallback(
		(newFilters: IProviderPayableLineListFilters) => {
			const merged = {
				...newFilters,
				...(fixedProviderId ? { providerIds: [fixedProviderId] } : {}),
			};
			setListFilters(merged);

			// Si el usuario elige manualmente fechas de schedule en los filtros, usarlas y limpiar el preset
			if (newFilters.scheduleDateFrom || newFilters.scheduleDateTo) {
				if (newFilters.scheduleDateFrom) setScheduleDateFrom(newFilters.scheduleDateFrom);
				if (newFilters.scheduleDateTo) setScheduleDateTo(newFilters.scheduleDateTo);
				setActivePreset('');
			}
		},
		[fixedProviderId],
	);

	const handleClearFilters = useCallback(() => {
		setListFilters(fixedProviderId ? { providerIds: [fixedProviderId] } : {});
	}, [fixedProviderId]);

	const totalGeneral = groups.reduce((acc, g) => acc + g.totalAmount, 0);
	const totalLines = groups.reduce((acc, g) => acc + g.lines.length, 0);

	return (
		<Box>
			<ScheduleQuickDateBar activePreset={activePreset} onPresetToggle={handlePresetToggle} />

			<ProviderPayablesFilters
				onFiltersChange={handleFiltersChange}
				onClearFilters={handleClearFilters}
				hideProviderFilter={!!fixedProviderId}
				embedded={!!fixedProviderId}
			/>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{!scheduleDateFrom || !scheduleDateTo ? (
				<Alert severity="info" sx={{ mb: 2 }}>
					Seleccioná un rango de fechas de vencimiento programado para ver la agenda.
				</Alert>
			) : loading ? (
				<Box display="flex" justifyContent="center" py={6}>
					<CircularProgress />
				</Box>
			) : (
				<>
					{/* Resumen general */}
					{groups.length > 0 && (
						<Paper
							variant="outlined"
							sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'grey.50', display: 'flex', gap: 3, flexWrap: 'wrap' }}
						>
							<Box>
								<Typography variant="caption" color="text.secondary" fontWeight={600}>
									Total del período
								</Typography>
								<Typography variant="h6" fontWeight={700} color="primary.main">
									USD {totalGeneral.toLocaleString('es-UY', { minimumFractionDigits: 2 })}
								</Typography>
							</Box>
							<Box>
								<Typography variant="caption" color="text.secondary" fontWeight={600}>
									Fechas de vencimiento
								</Typography>
								<Typography variant="h6" fontWeight={700}>
									{groups.length}
								</Typography>
							</Box>
							<Box>
								<Typography variant="caption" color="text.secondary" fontWeight={600}>
									Líneas totales
								</Typography>
								<Typography variant="h6" fontWeight={700}>
									{totalLines}
								</Typography>
							</Box>
						</Paper>
					)}

					{groups.length === 0 ? (
						<Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
							<Typography color="text.secondary">
								No hay líneas con vencimientos programados en el rango seleccionado.
							</Typography>
						</Paper>
					) : (
						<Grid container spacing={0}>
							{groups.map((group) => (
								<Grid item xs={12} key={group.schedulePaymentDate}>
									<ScheduleGroupCard group={group} defaultExpanded={groups.length <= 5} />
								</Grid>
							))}
						</Grid>
					)}
				</>
			)}
		</Box>
	);
};
